import { useState, useEffect } from 'react';
import {
  PhoneOutgoing,
  Settings,
  Users,
  CheckCircle,
  XCircle,
  Save,
  Trash2,
  Plus,
  TestTube,
  Eye,
  EyeOff,
  Info
} from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import Button from '../../../components/common/Button';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastMessage } from '@/utils/toast-message';
import { apiRequest } from '@/utils/api-request';

// Provider interface
interface Provider {
  code: string;
  name: string;
  description: string;
  defaultEndpoint: string;
  requiredFields: string[];
}

// Configuration interface
interface OutboundCallConfig {
  uuid?: string;
  provider: string;
  provider_name: string;
  api_endpoint: string;
  authorization_token: string;
  api_key?: string;
  k_number?: string;
  caller_id?: string;
  call_timeout: number;
  enable_recording: boolean;
  is_active: boolean;
}

// Bridge number interface
interface BridgeNumber {
  uuid: string;
  user_uuid: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  bridge_number: string;
  is_active: boolean;
  created_at: string;
}

// Validation schema
const configSchema = yup.object({
  provider: yup.string().required('Provider is required'),
  api_endpoint: yup.string().url('Must be a valid URL').required('API Endpoint is required'),
  authorization_token: yup.string().required('Authorization token is required'),
  api_key: yup.string(),
  k_number: yup.string(),
  caller_id: yup.string()
}).required();

type ConfigFormData = yup.InferType<typeof configSchema>;

export default function OutboundCallingSettings() {
  const [activeTab, setActiveTab] = useState<'config' | 'bridge-numbers'>('config');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [config, setConfig] = useState<OutboundCallConfig | null>(null);
  const [bridgeNumbers, setBridgeNumbers] = useState<BridgeNumber[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showDeleteConfigModal, setShowDeleteConfigModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<ConfigFormData>({
    resolver: yupResolver(configSchema),
    mode: 'onSubmit'
  });

  const selectedProvider = watch('provider');

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'config') {
      fetchProviders();
      fetchConfiguration();
    } else if (activeTab === 'bridge-numbers') {
      fetchBridgeNumbers();
    }
  }, [activeTab]);

  // Auto-switch to config tab if bridge-numbers tab is active but provider is not tata_tele
  useEffect(() => {
    if (activeTab === 'bridge-numbers' && config?.provider !== 'tata_tele') {
      setActiveTab('config');
    }
  }, [config?.provider, activeTab]);

  const fetchProviders = async () => {
    try {
      const response = await apiRequest('/api/outbound-call/providers', {
        method: 'GET'
      });

      if (response.success) {
        setProviders(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  };

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/outbound-call/config', {
        method: 'GET'
      });

      // Only set config if data is not null (API returns null when no config exists)
      if (response.success && response.data && response.data.uuid) {
        setConfig(response.data);
        reset({
          provider: response.data.provider,
          api_endpoint: response.data.api_endpoint,
          authorization_token: response.data.authorization_token,
          api_key: response.data.api_key || '',
          k_number: response.data.k_number || '',
          caller_id: response.data.caller_id || ''
        });
      } else {
        setConfig(null);
      }
    } catch (error) {
      console.error('Failed to fetch configuration:', error);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchBridgeNumbers = async () => {
    try {
      const response = await apiRequest('/api/outbound-call/bridge-numbers', {
        method: 'GET'
      });

      if (response.success) {
        setBridgeNumbers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch bridge numbers:', error);
    }
  };

  const onSubmit = async (data: ConfigFormData) => {
    try {
      setLoading(true);

      // Ensure all fields are included, even if empty
      const payload = {
        provider: data.provider,
        api_endpoint: data.api_endpoint,
        authorization_token: data.authorization_token,
        api_key: data.api_key || null,
        k_number: data.k_number || null,
        caller_id: data.caller_id || null
      };

      const response = await apiRequest('/api/outbound-call/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.success) {
        toastMessage(response.message || 'Configuration saved successfully', 'success');
        fetchConfiguration();
      } else {
        toastMessage(response.message || 'Failed to save configuration', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to save configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);

      const response = await apiRequest('/api/outbound-call/config/test', {
        method: 'POST'
      });

      if (response.success) {
        toastMessage('Connection test successful!', 'success');
      } else {
        toastMessage(response.message || 'Connection test failed', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Connection test failed', 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const confirmDeleteConfiguration = async () => {
    try {
      setLoading(true);

      const response = await apiRequest('/api/outbound-call/config', {
        method: 'DELETE'
      });

      if (response.success) {
        toastMessage('Configuration deleted successfully', 'success');
        setConfig(null);
        reset({});
        setShowDeleteConfigModal(false);
      } else {
        toastMessage(response.message || 'Failed to delete configuration', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to delete configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ModernSettingHeader
        title="Outbound Calling"
        description="Configure outbound call providers and manage user bridge numbers"
        icon={<PhoneOutgoing className="h-5 w-5 text-white" />}
      />

      <div className="p-6">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('config')}
              className={`${
                activeTab === 'config'
                  ? 'border-brandColor text-brandColor'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Settings className="w-4 h-4" />
              Provider Configuration
            </button>
            {/* Only show Bridge Numbers tab for Tata Tele (Smartflo) */}
            {config?.provider === 'tata_tele' && (
              <button
                onClick={() => setActiveTab('bridge-numbers')}
                className={`${
                  activeTab === 'bridge-numbers'
                    ? 'border-brandColor text-brandColor'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
              >
                <Users className="w-4 h-4" />
                Bridge Numbers
              </button>
            )}
          </nav>
        </div>

        {/* Provider Configuration Tab */}
        {activeTab === 'config' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Current Status */}
              {config && (
                <div className="mb-6 p-4 bg-brandColor/10 border border-brandColor/30 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-brandColor" />
                    <div>
                      <h3 className="font-medium text-brandColor">Configuration Active</h3>
                      <p className="text-sm text-brandColor mt-1">
                        Provider: {config.provider_name}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowDeleteConfigModal(true)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 flex-shrink-0"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Delete
                  </Button>
                </div>
              )}

              {/* Configuration Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telephony Provider *
                </label>
                <select
                  {...register('provider')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                >
                  <option value="">Select a provider</option>
                  {providers.map((provider) => (
                    <option key={provider.code} value={provider.code}>
                      {provider.name} - {provider.description}
                    </option>
                  ))}
                </select>
                {errors.provider && (
                  <p className="mt-1 text-sm text-red-600">{errors.provider.message}</p>
                )}
              </div>

              {/* Show form fields only when provider is selected */}
              {(selectedProvider || config) && (
                <>
                  {/* API Endpoint */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Endpoint *
                    </label>
                    <input
                      type="text"
                      {...register('api_endpoint')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                      placeholder="https://api.provider.com/v1/call"
                    />
                    {errors.api_endpoint && (
                      <p className="mt-1 text-sm text-red-600">{errors.api_endpoint.message}</p>
                    )}
                  </div>

              {/* Authorization Token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authorization Token *
                </label>
                <div className="relative">
                  <input
                    type={showTokens ? 'text' : 'password'}
                    {...register('authorization_token')}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter authorization token"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTokens(!showTokens)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showTokens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.authorization_token && (
                  <p className="mt-1 text-sm text-red-600">{errors.authorization_token.message}</p>
                )}
              </div>

              {/* Knowlarity-specific fields */}
              {selectedProvider === 'knowlarity' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key *
                    </label>
                    <input
                      type={showTokens ? 'text' : 'password'}
                      {...register('api_key')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                      placeholder="Enter API key"
                    />
                    {errors.api_key && (
                      <p className="mt-1 text-sm text-red-600">{errors.api_key.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      K-Number
                    </label>
                    <input
                      type="text"
                      {...register('k_number')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                      placeholder="Enter K-Number"
                    />
                    {errors.k_number && (
                      <p className="mt-1 text-sm text-red-600">{errors.k_number.message}</p>
                    )}
                  </div>

                  {/* Caller ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caller ID (Display Number)
                    </label>
                    <input
                      type="text"
                      {...register('caller_id')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                      placeholder="Enter caller ID number"
                    />
                    {errors.caller_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.caller_id.message}</p>
                    )}
                  </div>
                </>
              )}
                </>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
                <Button
                  type="submit"
                  disabled={loading}
                  leftIcon={<Save className="w-4 h-4" />}
                >
                  {loading ? 'Saving...' : config ? 'Update Config' : 'Save Config'}
                </Button>

                {config && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={testConnection}
                    disabled={testingConnection}
                    leftIcon={<TestTube className="w-4 h-4" />}
                  >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                  </Button>
                )}
              </div>
            </form>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Important Notes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Only one provider configuration is allowed per client</li>
                      <li>Credentials are encrypted and stored securely</li>
                      <li>Test the connection after saving to ensure proper setup</li>
                      {config?.provider === 'tata_tele' && (
                        <li>Configure user bridge numbers in the Bridge Numbers tab (required for Tata Tele)</li>
                      )}
                      <li>Call recordings and CDR data are managed by the provider</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bridge Numbers Tab - Only for Tata Tele */}
        {activeTab === 'bridge-numbers' && config?.provider === 'tata_tele' && (
          <BridgeNumbersTab
            bridgeNumbers={bridgeNumbers}
            onRefresh={fetchBridgeNumbers}
          />
        )}
      </div>

      {/* Delete Configuration Modal */}
      {showDeleteConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Configuration</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Are you sure you want to delete the <strong>{config?.provider_name}</strong> configuration?
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone. All outbound calling functionality using this provider will be disabled.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfigModal(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteConfiguration}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? 'Deleting...' : 'Delete Configuration'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bridge Numbers Tab Component
function BridgeNumbersTab({
  bridgeNumbers,
  onRefresh
}: {
  bridgeNumbers: BridgeNumber[];
  onRefresh: () => void;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Array<{ value: string; label: string; email: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; uuid: string; name: string }>({
    show: false,
    uuid: '',
    name: ''
  });

  const bridgeSchema = yup.object({
    user_uuid: yup.string().required('User is required'),
    user_phone: yup.string().required('User phone is required'),
    bridge_number: yup.string().required('Bridge number is required')
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(bridgeSchema)
  });

  // Fetch users when form is shown
  useEffect(() => {
    if (showAddForm && users.length === 0) {
      fetchUsers();
    }
  }, [showAddForm]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiRequest('/api/users/active/dropdown', {
        method: 'GET'
      });

      if (response.success && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const response = await apiRequest('/api/outbound-call/bridge-numbers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.success) {
        toastMessage('Bridge number added successfully', 'success');
        reset();
        setShowAddForm(false);
        onRefresh();
      } else {
        toastMessage(response.message || 'Failed to add bridge number', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to add bridge number', 'error');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/outbound-call/bridge-numbers/${deleteModal.uuid}`, {
        method: 'DELETE'
      });

      if (response.success) {
        toastMessage('Bridge number deleted successfully', 'success');
        setDeleteModal({ show: false, uuid: '', name: '' });
        onRefresh();
      } else {
        toastMessage(response.message || 'Failed to delete bridge number', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to delete bridge number', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900">User Bridge Numbers</h3>
            <p className="text-sm text-gray-600 mt-1">
              Map user phone numbers to bridge extensions for outbound calling
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Bridge Number
          </Button>
        </div>

        {/* Add Form */}
        {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User *
                </label>
                <select
                  {...register('user_uuid')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                  disabled={loadingUsers}
                >
                  <option value="">Select a user</option>
                  {users.map((user) => (
                    <option key={user.value} value={user.value}>
                      {user.label} ({user.email})
                    </option>
                  ))}
                </select>
                {errors.user_uuid && (
                  <p className="mt-1 text-sm text-red-600">{errors.user_uuid.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Phone *
                </label>
                <input
                  type="text"
                  {...register('user_phone')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                  placeholder="Enter user phone"
                />
                {errors.user_phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.user_phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bridge Number *
                </label>
                <input
                  type="text"
                  {...register('bridge_number')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                  placeholder="Enter bridge number"
                />
                {errors.bridge_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.bridge_number.message}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={loading} size="sm">
                {loading ? 'Adding...' : 'Add Bridge Number'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Bridge Numbers List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bridge Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bridgeNumbers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No bridge numbers configured yet
                </td>
              </tr>
            ) : (
              bridgeNumbers.map((bn) => (
                <tr key={bn.uuid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{bn.user_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{bn.user_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{bn.user_phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-brandColor">{bn.bridge_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {bn.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => setDeleteModal({ show: true, uuid: bn.uuid, name: bn.user_name || 'this user' })}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Bridge Number</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Are you sure you want to delete the bridge number mapping for <strong>{deleteModal.name}</strong>?
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone. The user will no longer be able to make outbound calls using this bridge number.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteModal({ show: false, uuid: '', name: '' })}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
